import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap, Quote } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useUI } from "../context/UIContext";

const POINTS = ["15+ Jahre Erfahrung", "Unabhängig & neutral", "Kein Callcenter", "Deutschlandweit"];

export const MeetBeraterin = () => {
  const { openUpload } = useUI();
  return (
    <section
      className="relative overflow-hidden py-24 sm:py-28"
      style={{ background: "linear-gradient(135deg,#0b1e46 0%,#12307a 45%,#1d4ed8 100%)" }}
      data-testid="meet-beraterin"
    >
      {/* Energy background layers */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {/* energy background image (abstract waves + wind turbines) */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-80"
          style={{ backgroundImage: "url(/energy-bg.jpg)" }}
        />
        {/* soft overlay only behind the text column to keep it readable */}
        <div className="absolute inset-0 bg-[#0b1e46]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1e46]/80 via-[#0b1e46]/10 to-[#0b1e46]/50" />
        {/* radial glow */}
        <div className="absolute -top-32 -right-24 h-[420px] w-[420px] rounded-full bg-blue-400/25 blur-[110px]" />
        <div className="absolute -bottom-40 -left-24 h-[420px] w-[420px] rounded-full bg-cyan-400/15 blur-[120px]" />
        {/* diagonal energy streaks */}
        <motion.div
          initial={{ opacity: 0.25 }}
          animate={{ opacity: [0.2, 0.45, 0.2] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent"
        />
        <motion.div
          initial={{ opacity: 0.15 }}
          animate={{ opacity: [0.1, 0.35, 0.1] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute top-2/3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200/50 to-transparent"
        />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 sm:px-10 text-center">
        {/* Eyebrow */}
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm px-4 py-1.5 text-xs uppercase tracking-[0.28em] text-cyan-200 mb-8" data-testid="beraterin-eyebrow">
            <Zap className="h-3.5 w-3.5" /> Persönlich für Sie da
          </span>
        </Reveal>

        {/* Small round photo with glowing ring */}
        <Reveal delay={0.05}>
          <div className="relative inline-block mb-7">
            <motion.span
              animate={{ opacity: [0.5, 0.9, 0.5], scale: [1, 1.06, 1] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 rounded-full bg-cyan-400/40 blur-xl"
              aria-hidden="true"
            />
            <img
              src="/kerstin-hemming.jpg"
              alt="Kerstin Hemming – Ihre persönliche, unabhängige Energieberaterin"
              className="relative h-36 w-36 sm:h-40 sm:w-40 rounded-full object-cover object-top ring-4 ring-white/80 shadow-2xl"
              loading="lazy"
              data-testid="beraterin-photo"
            />
          </div>
        </Reveal>

        {/* Name */}
        <Reveal delay={0.1}>
          <p className="text-cyan-200 text-sm tracking-wide mb-2">Kerstin Hemming · Energieberaterin</p>
          {/* Headline in elegant serif (different font) */}
          <h2 className="font-serif italic text-white text-3xl sm:text-5xl leading-tight mb-6" data-testid="beraterin-heading">
            Ein Gesicht, dem Sie vertrauen können
          </h2>
        </Reveal>

        {/* Quote */}
        <Reveal delay={0.15}>
          <div className="relative max-w-2xl mx-auto mb-8">
            <Quote className="h-6 w-6 text-cyan-300/50 mx-auto mb-3" />
            <p className="text-lg sm:text-xl text-blue-50/90 leading-relaxed font-light">
              „Ich vergleiche nicht nur Zahlen – ich nehme mir Zeit für Ihre Situation. Ehrlich, unabhängig und ohne Fachchinesisch, bis Sie wirklich sparen."
            </p>
          </div>
        </Reveal>

        {/* Trust chips */}
        <Reveal delay={0.2}>
          <div className="flex flex-wrap justify-center gap-2.5 mb-10">
            {POINTS.map((p, i) => (
              <span key={i} className="rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-sm text-white/90" data-testid={`beraterin-point-${i}`}>
                {p}
              </span>
            ))}
          </div>
        </Reveal>

        {/* CTA */}
        <Reveal delay={0.25}>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={openUpload}
              className="group inline-flex items-center gap-2 rounded-full bg-white text-blue-800 hover:bg-cyan-50 px-8 py-4 font-semibold transition-colors shadow-lg"
              data-testid="beraterin-cta"
            >
              Kostenlos beraten lassen
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <Link to="/ueber-uns" className="text-cyan-200 font-medium hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="beraterin-about-link">
              Mehr über mich <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
